import React, { useEffect, useRef, useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { 
  ClipboardList, 
  MapPin, 
  Calculator, 
  FileCheck, 
  Monitor, 
  CheckCircle,
  TreePine
} from 'lucide-react';

const stepIcons = [
  ClipboardList,
  MapPin,
  Calculator,
  FileCheck,
  Monitor,
  CheckCircle,
];

const Process = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const sectionRef = useRef(null);

  const { t } = useLanguage();
  const processSteps = t('process.steps', []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Auto-advance active step for visual effect
  useEffect(() => {
    if (isVisible && processSteps.length) {
      const interval = setInterval(() => {
        setActiveStep((prev) => (prev + 1) % processSteps.length);
      }, 3000);
      return () => clearInterval(interval);
    }
    return undefined;
  }, [isVisible, processSteps.length]);

  return (
    <section id="process" className="py-24 section-canopy relative overflow-hidden" ref={sectionRef} data-testid="process-section">
      {/* Decorative elements */}
      <div className="absolute inset-0 forest-grid" />
      <div className="absolute top-10 left-10 opacity-10">
        <TreePine className="w-32 h-32 text-[#1F3D2B]" />
      </div>
      <div className="absolute bottom-10 right-10 opacity-10">
        <TreePine className="w-24 h-24 text-[#1F3D2B]" />
      </div>
      <div className="absolute top-20 left-1/2 w-40 h-2 forest-branch animate-sway" style={{ animationDelay: '1s' }} />
      
      {/* Animated circles */}
      <div className="absolute top-1/4 right-1/4 w-64 h-64 bg-[#1F3D2B]/8 rounded-full blur-3xl animate-blob" />
      <div className="absolute bottom-1/4 left-1/4 w-48 h-48 bg-[#1F3D2B]/6 rounded-full blur-3xl animate-blob" style={{ animationDelay: '4s' }} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className={`text-center sm:text-left mb-16 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} data-testid="process-header">
          <span className="inline-block text-[#1F3D2B] font-medium text-sm tracking-wider uppercase mb-4 px-4 py-1 bg-[#1F3D2B]/10 rounded-full" data-testid="process-eyebrow">
            {t('process.eyebrow', 'Sadarbības process')}
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold text-[#2E2E2E] mb-4" data-testid="process-title">
            {t('process.title', 'Kā notiek sadarbība')}
          </h2>
          <p className="text-[#2E2E2E]/70 text-lg max-w-2xl mx-auto sm:mx-0" data-testid="process-description">
            {t('process.description', 'Skaidrs un pārskatāms process no pieteikuma līdz pabeigšanai.')}
          </p>
        </div>

        {/* Process Steps */}
        <div className="relative">
          <div className="hidden md:block absolute left-1/2 top-0 -translate-x-1/2 h-full w-px bg-[#1F3D2B]/15" data-testid="process-timeline-line" />
          <div className="space-y-10 md:space-y-14">
            {processSteps.map((step, index) => {
              const IconComponent = stepIcons[index % stepIcons.length];
              const isActive = index === activeStep;
              const isEven = index % 2 === 0;

              return (
                <div
                  key={step.id}
                  className={`relative flex items-start gap-4 md:gap-0 ${isEven ? 'md:justify-start' : 'md:justify-end'} ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'} transition-all duration-700`}
                  style={{ transitionDelay: `${index * 120}ms` }}
                  data-testid={`process-step-row-${step.id}`}
                >
                  {/* Mobile marker */}
                  <div className="md:hidden flex flex-col items-center mt-2" data-testid={`process-step-marker-mobile-${step.id}`}>
                    <div
                      className={`w-10 h-10 rounded-full bg-[#1F3D2B] text-white font-semibold flex items-center justify-center shadow-lg transition-all duration-500 ${
                        isActive ? 'scale-110 animate-pulse-glow' : ''
                      }`}
                      data-testid={`process-step-number-${step.id}-mobile`}
                    >
                      {step.id}
                    </div>
                    {index < processSteps.length - 1 && (
                      <div className="w-px flex-1 bg-[#1F3D2B]/20 mt-2" data-testid={`process-step-connector-${step.id}-mobile`} />
                    )}
                  </div>

                  <div
                    className={`forest-panel card-shine rounded-2xl p-6 md:w-[45%] transition-all duration-500 cursor-pointer group ${
                      isActive ? 'shadow-xl ring-2 ring-[#1F3D2B]/20 scale-[1.02]' : 'hover:shadow-lg hover:-translate-y-1'
                    } ${isEven ? 'md:pr-10 md:mr-auto' : 'md:pl-10 md:ml-auto'}`}
                    onMouseEnter={() => setActiveStep(index)}
                    data-testid={`process-step-card-${step.id}`}
                  >
                    <div className="flex items-start gap-4">
                      <div
                        className={`w-12 h-12 rounded-xl flex items-center justify-center transition-all duration-500 ${
                          isActive ? 'bg-[#1F3D2B] scale-110' : 'bg-[#1F3D2B]/10 group-hover:bg-[#1F3D2B]/20'
                        }`}
                        data-testid={`process-step-icon-${step.id}`}
                      >
                        <IconComponent
                          className={`w-6 h-6 transition-colors duration-500 ${
                            isActive ? 'text-white' : 'text-[#1F3D2B]'
                          }`}
                        />
                      </div>
                      <div>
                        <h3
                          className={`text-lg font-semibold mb-2 transition-colors duration-300 ${
                            isActive ? 'text-[#1F3D2B]' : 'text-[#2E2E2E]'
                          }`}
                          data-testid={`process-step-title-${step.id}`}
                        >
                          {step.title}
                        </h3>
                        <p className="text-[#2E2E2E]/60 text-sm leading-relaxed" data-testid={`process-step-description-${step.id}`}>
                          {step.description}
                        </p>
                      </div>
                    </div>
                    <div
                      className={`mt-4 h-1 bg-gray-100 rounded-full overflow-hidden ${isActive ? 'opacity-100' : 'opacity-0'}`}
                      data-testid={`process-step-progress-${step.id}`}
                    >
                      <div className="h-full bg-[#1F3D2B] rounded-full animate-progress" style={{ '--progress-width': '100%' }} />
                    </div>
                  </div>

                  {/* Desktop marker */}
                  <div className="hidden md:flex absolute left-1/2 -translate-x-1/2 top-4 flex-col items-center" data-testid={`process-step-marker-${step.id}`}>
                    <div
                      className={`w-12 h-12 rounded-full bg-[#1F3D2B] text-white font-bold flex items-center justify-center text-lg shadow-lg ring-4 ring-white transition-all duration-500 ${
                        isActive ? 'scale-110 animate-pulse-glow' : ''
                      }`}
                      data-testid={`process-step-number-${step.id}-desktop`}
                    >
                      {step.id}
                    </div>
                    {index < processSteps.length - 1 && (
                      <div className="w-px h-16 bg-[#1F3D2B]/20 mt-3" data-testid={`process-step-connector-${step.id}-desktop`} />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Bottom CTA */}
        <div className={`mt-16 text-center transition-all duration-700 delay-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <a
            href="#kontakti"
            className="inline-flex items-center justify-center px-8 py-4 forest-cta font-semibold transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 group"
            data-testid="process-cta"
          >
            {t('process.cta', 'Sākt sadarbību')}
            <svg className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform duration-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </a>
        </div>
      </div>
    </section>
  );
};

export default Process;
